import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { MainLayout } from '../layouts';
import { logger } from '../lib/logging';

interface FeedbackItem {
  id: string;
  category: 'bug' | 'feature' | 'idea' | 'other';
  details: string;
  pageUrl: string;
  comparisonId: string | null;
  comparisonTitle: string | null;
  modelSettingsJson: string | null;
  correlationId: string;
  userAgent: string | null;
  clientInfo: string | null;
  userId: string | null;
  userEmail: string;
  createdAt: string;
  updatedAt: string;
}

interface FeedbackResponse {
  success: boolean;
  feedback: FeedbackItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  stats: {
    categories: Array<{ category: string; count: number }>;
  };
}

interface AdminFeedbackPageProps {
  embedded?: boolean;
  correlationId?: string;
}

export const AdminFeedbackPage: React.FC<AdminFeedbackPageProps> = ({ embedded = false, correlationId }) => {
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [stats, setStats] = useState<{ category: string; count: number }[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const correlationIdValue = useMemo(() => correlationId ?? crypto.randomUUID(), [correlationId]);
  const ctx = useMemo(() => logger.withCorrelation(correlationIdValue), [correlationIdValue]);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    setError(null);

    const aggregated: FeedbackItem[] = [];
    const categoryTotals = new Map<string, number>();
    let offset = 0;
    let hasMore = true;
    let total = 0;
    const limit = 200;

    try {
      while (hasMore) {
        const params = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
        });

        if (categoryFilter) {
          params.append('category', categoryFilter);
        }

        const response = await fetch(`/api/admin/feedback?${params.toString()}`);

        if (!response.ok) {
          throw new Error('Failed to fetch feedback');
        }

        const data: FeedbackResponse = await response.json();
        aggregated.push(...data.feedback);

        data.feedback.forEach((item) => {
          categoryTotals.set(item.category, (categoryTotals.get(item.category) ?? 0) + 1);
        });

        total = data.pagination?.total ?? total ?? aggregated.length;
        offset += data.pagination?.limit ?? limit;
        hasMore = Boolean(data.pagination?.hasMore) && data.feedback.length > 0;

        if (!hasMore) {
          break;
        }
      }

      setFeedback(aggregated);
      setStats(Array.from(categoryTotals.entries()).map(([category, count]) => ({ category, count })));
      setTotalCount(total || aggregated.length);
      setLastUpdated(new Date());

      logger.info('admin.feedback_refresh_success', undefined, {
        ...ctx,
        component: 'AdminFeedbackPage',
        categoryFilter: categoryFilter || 'all',
        resultCount: aggregated.length,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);

      logger.error('admin.feedback_refresh_error', undefined, {
        ...ctx,
        component: 'AdminFeedbackPage',
        categoryFilter: categoryFilter || 'all',
        err_message: message,
      });
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, ctx]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const content = (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Admin Feedback</h1>
            <p className="text-slate-300">All submissions from the results feedback module.</p>
            <p className="text-sm text-slate-400">
              Showing {feedback.length} of {totalCount || feedback.length} entries
              {categoryFilter ? ` (filtered by ${categoryFilter})` : ''}
              {lastUpdated ? ` • Updated ${lastUpdated.toLocaleTimeString()}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              className="bg-slate-800 text-slate-100 border border-slate-700 rounded-lg px-3 py-2 text-sm"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All categories</option>
              {['bug', 'feature', 'idea', 'other'].map((option) => (
                <option key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </option>
              ))}
            </select>
            <button
              onClick={fetchFeedback}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
          <div className="p-4 bg-slate-800/40 border border-slate-700/60 rounded-xl">
            <div className="text-sm text-slate-400">Total</div>
            <div className="text-2xl font-semibold text-white">{totalCount || feedback.length}</div>
          </div>
          {stats.map((stat) => (
            <div key={stat.category} className="p-4 bg-slate-800/40 border border-slate-700/60 rounded-xl">
              <div className="text-sm text-slate-400 capitalize">{stat.category}</div>
              <div className="text-2xl font-semibold text-white">{stat.count}</div>
            </div>
          ))}
        </div>

        <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/60 rounded-2xl p-4">
          {loading && <div className="text-center py-8 text-slate-400">Loading feedback...</div>}

          {error && !loading && (
            <div className="p-4 bg-red-900/30 border border-red-700/60 rounded-lg text-red-200">Error: {error}</div>
          )}

          {!loading && !error && feedback.length === 0 && (
            <div className="text-center py-8 text-slate-400">No feedback submissions yet.</div>
          )}

          {!loading && !error && feedback.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-slate-200">
                <thead className="text-xs uppercase text-slate-400 border-b border-slate-700/60">
                  <tr>
                    <th className="py-3 pr-4">Created</th>
                    <th className="py-3 pr-4">Category</th>
                    <th className="py-3 pr-4">User</th>
                    <th className="py-3 pr-4">Details</th>
                    <th className="py-3 pr-4">Page</th>
                    <th className="py-3 pr-4">Comparison</th>
                    <th className="py-3 pr-4">Correlation ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {feedback.map((item) => (
                    <tr key={item.id} className="align-top">
                      <td className="py-3 pr-4 text-slate-300 whitespace-nowrap">
                        {new Date(item.createdAt).toLocaleString()}
                      </td>
                      <td className="py-3 pr-4">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-lg border ${
                            item.category === 'bug'
                              ? 'bg-red-900/30 border-red-500/50 text-red-200'
                              : item.category === 'feature'
                              ? 'bg-blue-900/30 border-blue-500/50 text-blue-200'
                              : item.category === 'idea'
                              ? 'bg-purple-900/30 border-purple-500/50 text-purple-200'
                              : 'bg-slate-700/30 border-slate-500/50 text-slate-200'
                          }`}
                        >
                          {item.category}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-slate-300 whitespace-nowrap">{item.userEmail}</td>
                      <td className="py-3 pr-4 text-slate-100 whitespace-pre-wrap max-w-xl">{item.details}</td>
                      <td className="py-3 pr-4 text-slate-300 max-w-xs">
                        {item.pageUrl ? (
                          <a
                            href={item.pageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300 break-all"
                          >
                            {item.pageUrl}
                            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                          </a>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-slate-300 max-w-xs">
                        {item.comparisonTitle || item.comparisonId || <span className="text-slate-500">—</span>}
                      </td>
                      <td className="py-3 pr-4 text-slate-400 font-mono text-xs break-all">{item.correlationId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return embedded ? content : (
    <MainLayout>
      {content}
    </MainLayout>
  );
};

export default AdminFeedbackPage;
